import Link from "../../../components/common/LinkButton";
import { DemoUsageType } from "../../../shared/enums/DemoUsageTypeEnum";
import DownloadExcelExample from "./DownloadExcelExample";
import Highlighter from "../Highlighter";
import TableSpecifyOutputRequirements from "./Tables/SpecifyOutputRequirements";
import TableLabelYourInputData from "./Tables/LabelYourInputData";
import TableExplainNeededOutput from "./Tables/ExplainNeededOutput";
import { H2, H3 } from "../Headings";
import ExternalLink from "../ExternalLink";
import MSExcelSecurity1 from "../../../static/images/docs/view-examples/MSExcel_Security_1.png";
import MSExcelSecurity2 from "../../../static/images/docs/view-examples/MSExcel_Security_2.png";
import MSExcelSecurity3 from "../../../static/images/docs/view-examples/MSExcel_Security_3.png";
import MSExcelSecurity5 from "../../../static/images/docs/view-examples/MSExcel_Security_5.png";
import MSExcelExample1 from "../../../static/images/docs/view-examples/MSExcel_Example_1.png";
import MSExcelExample4 from "../../../static/images/docs/view-examples/MSExcel_Example_4.png";
import MSExcelExample5 from "../../../static/images/docs/view-examples/MSExcel_Example_5.png";
import MSExcelExample6 from "../../../static/images/docs/view-examples/MSExcel_Example_6.png";

interface Props {
  logDownload : (demoId: string, usageType: DemoUsageType) => void; 
  handleHowItWorksCLick: () => void;
  handleTokensCLick: () => void;
}

export default function MSExcelExampleDrescription(props: Props) {
  const { logDownload, handleHowItWorksCLick, handleTokensCLick } = props;

  return(
    <div>
      <div className="mt-8">
        <H2>MS Excel</H2>
      </div>
      <div>
        Each Excel file contains the four steps necessary to use the timePhasing service.
      </div>

      <div className="italic">
        To learn more about the 4 steps, we recommended you to read the {" "}
        <Link className="text-primaryRed" onClick={handleHowItWorksCLick}>
          “How it works”
        </Link> 
        {" "} page.
      </div>

      <div className="mt-4">
        You can either run the example using Input Data provided in the file, or you can replace them with your own to get started with time-phasing of your own data.
      </div>
      <div className="mt-4">
        Read detailed instructions below on how to use the files, or download files here:
      </div>
      
      <div className="flex py-4">
        <DownloadExcelExample logDownload={logDownload}/>
      </div>

      <H3>Security settings</H3>
      <div>
        To enable communication between the workbook and the timePhasing service, you must do the following:
      </div>

      <div className="mt-4 space-y-4">
        <div>
          <b>1. Enable Content from External Connections</b>
          <div className="ml-6">
            When opening the file for the first time, you will be prompted by Excel to “Enable Content” from External Sources for this Workbook.
          </div>
          <div className="mt-4 ml-6">
            <img src={MSExcelSecurity1} alt="timePhasing_MSExcelSecurity1" />
          </div>
        </div>

        <div>
          <b>2. Ignore Privacy levels</b>
          <div className="flex flex-wrap ml-6">
            <img src={MSExcelSecurity2} alt="timePhasing_MSExcelSecurity2" />
            <img src={MSExcelSecurity3} alt="timePhasing_MSExcelSecurity3" />
          </div>
        </div>

        <div>
          <b>3. Create and add token key</b>
          <div className="ml-6">
            To obtain a key, enter the {" "}
            <Link className="text-primaryRed" onClick={handleTokensCLick}>
              Tokens
            </Link> 
            {" "} page and register a new key. <br/>
            Add your personal key to the [Token key] field in [Input sheet] in workbook as shown below :
          </div>
          <div className="mt-4 ml-6">
            <TableSpecifyOutputRequirements />
          </div>
        </div>

        <div>
          <b>4. Grant permission for this workbook to access web content</b>
          <div className="ml-6">
            When you press the "Refresh All" button under Data, you will be prompted to allow this Workbook to Access Web Content from the {" "}
            <ExternalLink title="timePhasing.promineo.com" url={"https://timephasing.promineo.com/"} />
            {" "}site
            <div className="mt-4">
              <img src={MSExcelSecurity5} alt="timePhasing_MSExcelSecurity5" />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <Highlighter isMultiLine>
          IMPORTANT NOTE: If you don’t enable file, ignore privacy levels and accept web content, you can still review the file (including setup and details in Power Query).<br/>
          However, communication with the timePhasing service is not possible, and you will not see the effect of your changes in the output section.
        </Highlighter>
      </div>

      <div className="mt-4">
        <H3>Excel example files explained</H3>
      </div>
      <div>
        Each Excel example file contains the input data and a default configuration. It is put together in a step-by-step allowing you to play around with each step, and, in the end, review the output (the result) from the timePhasing service.
      </div>

      <div className="ml-4 mt-4 space-y-4">
        <div>
          <b>1. Provide input data</b>
          <div className="mt-4 ml-4">
            <img src={MSExcelExample1} alt="timePhasing_MSExcelExample1" />
          </div>
          <div className="mt-4 ml-4">
            Your input data can be a list of budget items, activities in a project schedule or any other list of items you want to time-phase. <br/>
            As a minimum, the timePhasing service expects you to provide a start date, finish date and value. <br/>
            If you want to group the resulting data, you must add additional field(s) to your input data. <br/>
          </div>
        </div>
        
        <div>
          <b>2. Make timePhasing understand your data</b>
          <div className="mt-4 ml-6">
            <TableLabelYourInputData />
          </div>
          <div className="mt-4 ml-6">
            The timePhasing service must be told which fields in your input data it will use for <b>start date</b>, <b>finish date</b> and <b>value</b>. <br/>
            If you are using calendars and/or profiles, <b>calendar key field</b> and/or <b>profile key field</b> are required. <br/>
          </div>
        </div>

        <div>
          <b>3. Specify output</b>
          <div className="mt-4 ml-6">
            <TableExplainNeededOutput />
          </div>

          <div className="mt-4 ml-6">
            Provide the timePhasing service with details on how you want the output to be shaped. <br/>
            Choose granularity of periods (daily, weekly, monthly, …). <br/>
            If you want your result the be grouped, specify which field in your input data to use for grouping. <br/>
          </div>
        </div>

        <div>
          <b>4. Review output</b>
          <div className="mt-4 ml-6">
            <img src={MSExcelExample4} alt="timePhasing_MSExcelExample4" />
          </div>
          <div className="mt-4 ml-6">
            After completing the 3 steps above, you are ready to call the timePhasing service and receive updated time-phased data.
          </div>
          <div className="mt-4 ml-6">
            <img src={MSExcelExample5} alt="timePhasing_MSExcelExample5" />
          </div>
          <div className="mt-4 ml-6">
            Press the [Refresh All] button (displayed above) under [Data] ribbon in Excel to request output from the timePhasing service. <br/>
            The resulting data is displayed in the table located to the left in illustration above.
          </div>

          <div className="mt-4 ml-6">
            <Highlighter isMultiLine>
              NOTE: The graph is a pivot graph in Excel added to visualize the received data from the service.
            </Highlighter>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <H3>Finally, look into how the magic happens!</H3>
      </div>
      <div>
        Open <b>Power Query</b> (shown in figure below) to dive into the details of how everything is connected. <br/>
        Here you will be given insights on how to

        <div className="ml-4 mt-4">
          <div>
            1. transform your input data and output specification into a proper request JSON <br/>
            2. call the timePhasing service with a proper request body (in JSON format) <br/>
            3. handle the response from the service (also in JSON format)
          </div>
        </div>
      </div>
      <div className="mt-4">
        <img src={MSExcelExample6} alt="timePhasing_MSExcelExample6" />
      </div>
    </div>
  );
}